import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useOidc } from '@axa-fr/react-oidc';
import flyLogo from '@fly/assets/images/fly_logo.svg';
import { Button, IconButton } from '@fly/core';
import { PlatformService } from '@fly/platform-service';
import { REGISTRATION_STORAGE_KEY, routes } from '@fly/shared';
import { FlySvgSprite } from '@fly/svg';
import { ScrollArea } from '@radix-ui/themes';
import cn from 'classnames';

interface DownloadLayoutProps {
  children: ReactNode;
  onDownload?: () => void;
  'data-testid'?: string;
  className?: string;
}

export function DownloadLayout({
  children,
  onDownload,
  'data-testid': testId,
  className = '',
}: DownloadLayoutProps) {
  const { t } = useTranslation();
  const { isAuthenticated, login } = useOidc();

  const handleSignIn = () => {
    localStorage.removeItem(REGISTRATION_STORAGE_KEY);
    PlatformService.getInstance().login(() => login(routes.DASHBOARD.ROOT));
  };

  return (
    <div className='bg-gray-1 flex h-full w-full flex-col' data-testid={testId}>
      <header className='border-gray-4 px-8 py-4 flex w-full items-center justify-between border-b'>
        <div className='flex items-center gap-[5px]'>
          <img src={flyLogo} alt='JFrog Fly' className='h-5 w-5' />
          <FlySvgSprite
            name='logo_text'
            className='text-gray-12'
            width={68}
            height={19}
          />
        </div>
        <div className='gap-2 flex items-center'>
          {isAuthenticated ? (
            <>
              <Button
                size='2'
                color='neutral'
                highContrast
                onClick={onDownload}
              >
                {t('app.download.download_button', {
                  defaultValue: 'Download',
                })}
              </Button>
              <IconButton
                color='neutral'
                variant='ghost'
                icon='home'
                to={routes.DASHBOARD.ROOT}
              />
            </>
          ) : (
            <>
              <Button
                size='2'
                color='neutral'
                variant='outline'
                onClick={handleSignIn}
              >
                {t('app.download.sign_in', { defaultValue: 'Sign In' })}
              </Button>
              <Button
                size='2'
                color='neutral'
                highContrast
                onClick={onDownload}
              >
                {t('app.download.download_button', {
                  defaultValue: 'Download',
                })}
              </Button>
            </>
          )}
        </div>
      </header>
      <ScrollArea scrollbars='vertical' type='auto'>
        <main
          className={cn(
            'p-4 pb-32 flex h-full flex-1 items-center justify-center',
            className
          )}
        >
          {children}
        </main>
      </ScrollArea>
    </div>
  );
}
